"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.projectDocumentToMomFields = projectDocumentToMomFields;
const choice_options_1 = require("./choice_options");
const signer_roles_1 = require("./signer_roles");
const template_custom_data_1 = require("./template_custom_data");
const ELEMENT_TYPE_TO_EC_TYPE = {
    DATE_INPUT: 'ec__date',
    CHOICE: 'ec__text_choice',
    SINGLE_SELECT: 'ec__text_choice',
    MULTI_SELECT: 'ec__text_choice',
};
/**
 * Serializes independently-positioned choice options into the Cortex `ec__selection_options` string,
 * keyed by the option's integer key so no signer-visible text is required (SSP-4085 / SSP-4107).
 *
 * Pairs are joined by `=,`, not `,`, matching the separator Cortex's own `ec__document_processor`
 * parses (`library.ec__document_processor.js`). A bare comma cannot be the delimiter here: the
 * reference value is free text copied from the document, and consent answers routinely contain a
 * comma ("Yes, I agree to participate"), which would otherwise split one option into two and leave
 * the saved answer unmatchable on read.
 */
function buildChoiceSelectionOptions(el) {
    var _a, _b;
    const options = (_a = el.config.ec__options) !== null && _a !== void 0 ? _a : [];
    if (options.length === 0) {
        return (_b = el.config.ec__selection_options) !== null && _b !== void 0 ? _b : '';
    }
    return options.map((option) => `${option.key}=${(0, choice_options_1.choiceOptionReferenceValue)(option)}`).join('=,');
}
/**
 * Projects the editor's ConsentElement[], ConsentRole[] and template custom fields into the
 * four MOM-facing arrays that Cortex expects on ec__document_template:
 *   - ec__requested_signatures  (SIGNATURE and INITIALS elements)
 *   - ec__requested_data        (TEXT_INPUT, DATE_INPUT, CHOICE, and any other non-signature elements)
 *   - ec__signer_roles          (document-level role definitions)
 *   - ec__custom_data           (document-level typed custom fields)
 *
 * Position/page fields are intentionally omitted — they are only needed for the
 * PDF overlay editor and are not part of the Cortex signing workflow.
 *
 * `ec__key` is used as the fallback for any element whose `ec__title` is
 * empty, since Cortex requires titles to be unique within each array.
 */
function projectDocumentToMomFields(elements, roles, customFields = []) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    const ec__requested_signatures = [];
    const ec__requested_data = [];
    for (const el of elements) {
        const c = el.config;
        if (el.type === 'SIGNATURE' || el.type === 'INITIALS') {
            ec__requested_signatures.push({
                ec__key: c.ec__key,
                ec__signer_role: (_a = c.ec__signer_role) !== null && _a !== void 0 ? _a : '',
                ec__title: c.ec__title || c.ec__key,
                ec__description: (_b = c.ec__description) !== null && _b !== void 0 ? _b : '',
                ec__label: (_c = c.ec__label) !== null && _c !== void 0 ? _c : '',
                ec__optional: (_d = c.ec__optional) !== null && _d !== void 0 ? _d : false,
                ec__initials: el.type === 'INITIALS',
                // Role list position is the signing order; leftover per-field values remain only when
                // the template has no roles yet (legacy / empty-role projections).
                ec__order: (_f = (_e = (0, signer_roles_1.signingOrderFromRoles)(c.ec__signer_role, roles)) !== null && _e !== void 0 ? _e : c.ec__order) !== null && _f !== void 0 ? _f : 0,
                ec__custom_data: (_g = c.ec__custom_data) !== null && _g !== void 0 ? _g : [],
            });
        }
        else {
            const isChoice = el.type === 'SINGLE_SELECT' || el.type === 'MULTI_SELECT';
            ec__requested_data.push({
                ec__key: c.ec__key,
                ec__signer_role: (_h = c.ec__signer_role) !== null && _h !== void 0 ? _h : '',
                ec__title: c.ec__title || c.ec__key,
                ec__description: (_j = c.ec__description) !== null && _j !== void 0 ? _j : '',
                ec__optional: (_k = c.ec__optional) !== null && _k !== void 0 ? _k : false,
                // TEXT_INPUT type is the author's choice; do not default to ec__text when unset.
                ec__type: el.type === 'TEXT_INPUT' ? c.ec__input_type : ((_l = ELEMENT_TYPE_TO_EC_TYPE[el.type]) !== null && _l !== void 0 ? _l : 'ec__text'),
                // A single-select is a text-choice with allow_multiple false; multi-select sets it true.
                ec__allow_multiple: isChoice ? el.type === 'MULTI_SELECT' : ((_m = c.ec__allow_multiple) !== null && _m !== void 0 ? _m : false),
                ec__selection_options: isChoice ? buildChoiceSelectionOptions(el) : ((_o = c.ec__selection_options) !== null && _o !== void 0 ? _o : ''),
                ec__custom_data: (_p = c.ec__custom_data) !== null && _p !== void 0 ? _p : [],
            });
        }
    }
    // A non-signing participant never signs, so it neither holds an order position nor is required.
    // Order comes from the same helper the Roles tab badge and the compiled signature fields use, which
    // is what keeps the three in step (SSP-4198).
    const orders = (0, signer_roles_1.signingOrders)(roles);
    const ec__signer_roles = roles.map((r, i) => ({
        ec__key: r.id,
        ec__role: (0, signer_roles_1.consentRoleLabel)(r),
        ec__signer_type: r.signerTypeId,
        ec__required: (0, signer_roles_1.isNonSigningSignerType)(r.signerTypeId) ? false : r.roleRequired,
        ec__order: orders[i],
    }));
    // The client-only `id` is dropped here: Cortex auto-generates ec__key on create.
    const ec__custom_data = (0, template_custom_data_1.projectableTemplateCustomFields)(customFields).map(({ ec__label, ec__type, ec__value }) => ({
        ec__label,
        ec__type,
        ec__value,
    }));
    return { ec__requested_signatures, ec__requested_data, ec__signer_roles, ec__custom_data };
}
//# sourceMappingURL=project_document_to_mom_fields.js.map